// Players sheet — the phone-sized alliance panel. Lists every other player
// (bots included — they own tank slots like humans, see desktop
// alliancePanel.ts), with an ally-status pip and one big thumb-target
// action button per row.
//
// Alliance semantics mirror the desktop panel exactly
// (packages/client/src/alliancePanel.ts draw()):
//   wantsMe = (state.wantAllies[them] >> me) & 1  — they asked me
//   iWant   = (state.wantAllies[me] >> them) & 1  — I asked them
//   active  = (state.allies[me] >> them) & 1      — server-derived mutual pact
// Both bitmaps arrive verbatim in every snapshot; `target` for
// requestAlly/cancelAlly is the OWNER SLOT NUMBER (0..MAX_PLAYERS-1), the
// same number the bitmaps are indexed by.
//
// requestAlly doubles as "accept" (mutual interest auto-resolves to an
// active pact server-side); cancelAlly withdraws a pending request or
// breaks an active pact — same toggle logic as the desktop rows.

import { playerLabel } from '@bolo2k/client-core';
import type { GameContext } from '../context.js';

export interface PlayersSheet {
  readonly el: HTMLElement;
  /** Rebuild the roster from ctx.state(). Signature-gated so the ~4 Hz
   *  poll while the sheet is open doesn't churn the DOM. */
  refresh(): void;
}

type AllyStatus = 'allied' | 'requested' | 'incoming' | 'none';

export function createPlayersSheet(ctx: GameContext, onAction: () => void): PlayersSheet {
  const el = document.createElement('div');
  el.className = 'cw-players';
  const list = document.createElement('div');
  list.className = 'cw-player-list';
  el.appendChild(list);

  let lastSig: string | null = null;

  function statusFor(
    state: NonNullable<ReturnType<GameContext['state']>>,
    owner: number,
  ): AllyStatus {
    const me = state.myOwner;
    const wantsMe = ((state.wantAllies[owner] ?? 0) & (1 << me)) !== 0;
    const iWant = ((state.wantAllies[me] ?? 0) & (1 << owner)) !== 0;
    const active = ((state.allies[me] ?? 0) & (1 << owner)) !== 0;
    // Desktop panel treats iWant && wantsMe as allied even before the
    // derived bitmap catches up (snapshot race) — keep the same edge case.
    if (active || (iWant && wantsMe)) return 'allied';
    if (iWant) return 'requested';
    if (wantsMe) return 'incoming';
    return 'none';
  }

  function makeRow(
    state: NonNullable<ReturnType<GameContext['state']>>,
    owner: number,
    status: AllyStatus,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'cw-row';

    const pip = document.createElement('span');
    pip.className = 'cw-pip';
    if (status === 'allied') pip.classList.add('cw-pip-active');
    else if (status !== 'none') pip.classList.add('cw-pip-pending');

    const name = document.createElement('span');
    name.className = 'cw-name';
    name.textContent = playerLabel(state, owner);

    const act = document.createElement('button');
    act.type = 'button';
    act.className = 'cw-act';
    switch (status) {
      case 'allied':
        act.textContent = 'Break alliance';
        act.classList.add('cw-act-break');
        break;
      case 'requested':
        act.textContent = 'Cancel request';
        act.classList.add('cw-act-cancel');
        break;
      case 'incoming':
        act.textContent = 'Accept';
        act.classList.add('cw-act-accept');
        break;
      default:
        act.textContent = 'Request ally';
    }
    act.addEventListener('click', () => {
      if (status === 'allied' || status === 'requested') ctx.cancelAlly(owner);
      else ctx.requestAlly(owner);
      lastSig = null; // force a rebuild next open
      onAction(); // dismiss-on-action
    });

    row.append(pip, name, act);
    return row;
  }

  function refresh(): void {
    const state = ctx.state();
    if (!state) {
      setEmpty('(connecting…)');
      return;
    }
    if (state.myOwner < 0) {
      // Spectators can watch but not ally (server ignores their requests).
      setEmpty('(spectating — no alliances)');
      return;
    }
    // Roster: anyone with a tank in the snapshot except me. Same derivation
    // as desktop alliancePanel.ts:94-96 — bots appear because they own
    // ordinary tank slots.
    const others = Array.from(new Set(state.tanks.map((t) => t.owner)))
      .filter((o) => o !== state.myOwner && o >= 0)
      .sort((a, b) => a - b);

    if (others.length === 0) {
      setEmpty('(no other players)');
      return;
    }

    const rows = others.map((o) => ({ owner: o, status: statusFor(state, o) }));
    const sig = rows.map((r) => `${r.owner}:${playerLabel(state, r.owner)}:${r.status}`).join('|');
    if (sig === lastSig) return;
    lastSig = sig;

    list.textContent = '';
    for (const r of rows) list.appendChild(makeRow(state, r.owner, r.status));
  }

  function setEmpty(msg: string): void {
    if (lastSig === msg) return;
    lastSig = msg;
    list.textContent = '';
    const empty = document.createElement('div');
    empty.className = 'cw-empty';
    empty.textContent = msg;
    list.appendChild(empty);
  }

  refresh();
  return { el, refresh };
}
